import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import type { OcppMessageEntry } from "../../../services/client/types.gen";

export const RANGE_PRESETS = {
  "1h": { label: "Last hour", hours: 1 },
  "24h": { label: "Last 24 hours", hours: 24 },
  "7d": { label: "Last 7 days", hours: 24 * 7 },
} as const;

export type RangePreset = keyof typeof RANGE_PRESETS;

const paramsFor = (prefix: string) => ({
  search: `${prefix}_search`,
  charger: `${prefix}_charger`,
  asset: `${prefix}_asset`,
  action: `${prefix}_action`,
  sender: `${prefix}_sender`,
  type: `${prefix}_type`,
  range: `${prefix}_range`,
  from: `${prefix}_from`,
  to: `${prefix}_to`,
  transaction: `${prefix}_transaction`,
});

const DEFAULT_PRESET: RangePreset = "24h";

const isRangePreset = (value: string | null): value is RangePreset =>
  value !== null && value in RANGE_PRESETS;

const isSender = (value: string | null): value is OcppMessageEntry["sender"] =>
  value === "charging_pole" || value === "cpms";

export const DATA_TYPES = ["measurements", "transactions"] as const;

export type DataType = (typeof DATA_TYPES)[number];

export const isDataType = (value: string): value is DataType =>
  (DATA_TYPES as readonly string[]).includes(value);

export type MessageFilterUpdate = {
  search?: string;
  chargers?: string[];
  assets?: string[];
  actions?: string[];
  sender?: OcppMessageEntry["sender"] | null;
  types?: DataType[];
  range?: RangePreset | { from: string; to: string };
};

// The filters live in URL search params named `<prefix>_<filter>`, so two logs
// on the same page (or a log inside a drawer) can keep their own filters.
export const useOcppMessageFilters = (prefix = "ocpp") => {
  const PARAMS = useMemo(() => paramsFor(prefix), [prefix]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshedAt, setRefreshedAt] = useState(() => Date.now());

  const search = searchParams.get(PARAMS.search) ?? "";
  const chargers = searchParams.getAll(PARAMS.charger);
  const assets = searchParams.getAll(PARAMS.asset);
  const actions = searchParams.getAll(PARAMS.action);
  const senderParam = searchParams.get(PARAMS.sender);
  const sender = isSender(senderParam) ? senderParam : null;
  const types = searchParams.getAll(PARAMS.type).filter(isDataType);
  const transactionId = searchParams.get(PARAMS.transaction);

  const fromParam = searchParams.get(PARAMS.from);
  const toParam = searchParams.get(PARAMS.to);
  const rangeParam = searchParams.get(PARAMS.range);
  const preset: RangePreset | null =
    fromParam && toParam
      ? null
      : isRangePreset(rangeParam)
        ? rangeParam
        : DEFAULT_PRESET;

  const receivedRange = useMemo(() => {
    if (preset === null) {
      return { after: fromParam!, before: toParam! };
    }
    return {
      after: dayjs(refreshedAt)
        .subtract(RANGE_PRESETS[preset].hours, "hour")
        .toISOString(),
      before: null,
    };
  }, [preset, fromParam, toParam, refreshedAt]);

  const update = useCallback(
    (changes: MessageFilterUpdate) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          const replaceAll = (key: string, values: string[]) => {
            next.delete(key);
            values.forEach((value) => next.append(key, value));
          };
          if (changes.search !== undefined) {
            replaceAll(PARAMS.search, changes.search ? [changes.search] : []);
          }
          if (changes.chargers !== undefined) {
            replaceAll(PARAMS.charger, changes.chargers);
          }
          if (changes.assets !== undefined) {
            replaceAll(PARAMS.asset, changes.assets);
          }
          if (changes.actions !== undefined) {
            replaceAll(PARAMS.action, changes.actions);
          }
          if (changes.sender !== undefined) {
            replaceAll(PARAMS.sender, changes.sender ? [changes.sender] : []);
          }
          if (changes.types !== undefined) {
            replaceAll(PARAMS.type, changes.types);
          }
          if (changes.range !== undefined) {
            next.delete(PARAMS.range);
            next.delete(PARAMS.from);
            next.delete(PARAMS.to);
            if (typeof changes.range === "string") {
              next.set(PARAMS.range, changes.range);
            } else {
              next.set(PARAMS.from, changes.range.from);
              next.set(PARAMS.to, changes.range.to);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [PARAMS, setSearchParams],
  );

  const setTransactionId = useCallback(
    (value: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value) {
            next.set(PARAMS.transaction, value);
          } else {
            next.delete(PARAMS.transaction);
          }
          return next;
        },
        { replace: true },
      );
    },
    [PARAMS, setSearchParams],
  );

  const refresh = useCallback(() => setRefreshedAt(Date.now()), []);

  return {
    search,
    chargers,
    assets,
    actions,
    sender,
    types,
    preset,
    receivedRange,
    transactionId,
    update,
    setTransactionId,
    refresh,
  };
};
